import React, { useState, useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Tooltip from "@mui/material/Tooltip";
import { Link, useLocation } from "react-router-dom";
import { ImageContext } from "../page/ApiContext.jsx";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "#88ADFF",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "1rem",
    boxShadow: theme.shadows[1],
    padding: "8px 12px",
  },
  "& .MuiTooltip-arrow": {
    color: "white",
  },
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function MiniDrawer({ isExpanded, setIsExpanded }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState(location.pathname);
  const username = localStorage.getItem("email");
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    dashboardIds,
    setDashboardIds,
    functionalityIds,
    setFunctionalityIds,
    userRoleId,
    setUserRoleId,
    roleActive,
    setRoleActive,
  } = useContext(ImageContext);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    document.cookie = `sqa_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    localStorage.clear();
    navigate("/auth/user-info");
    setRoleActive(false);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMouseEnter = () => {
    setOpen(true);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
    setIsExpanded(false);
  };

  const links = [
    {
      text: "Scraper Dashboard",
      icon: <SpaceDashboardIcon />,
      to: "/home",
    },
    {
      text: "Notifications",
      icon: <NotificationsActiveIcon />,
      to: "/notification",
    },
  ];

  // Conditionally add the Admin link
  if (dashboardIds.includes(101)) {
    links.push({
      text: "Admin",
      icon: <SupervisorAccountIcon />,
      to: "/adminDashboard",
    });
  }

  React.useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar sx={{ boxShadow: "none" }} position="fixed" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleMouseEnter}
            edge="start"
            sx={{
              position: "absolute",
              left: 25,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              paddingLeft: "15rem",
            }}
          >
            <Typography variant="h6" component="div">
              {location?.pathname === "/"
                ? "Scraper QA Dashboard"
                : location?.pathname === "/adminDashboard"
                ? "User Management"
                : "Scraper Notifications"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={handleClick}
          >
            <AccountCircleIcon />
            <Typography variant="body2" component="p">
              {username}
            </Typography>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{
              mt: 1,
              "& .MuiMenuItem-root": {
                fontSize: "0.875rem", // Smaller font size
                padding: "4px 8px", // Reduced padding
              },
            }}
          >
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: "1rem" }} />{" "}
              {/* Adjust icon size if needed */}
              <Typography variant="body2" component="span">
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
      >
        <DrawerHeader
          style={{ backgroundColor: "#88adff" }}
          sx={{ boxShadow: "none" }}
        >
          <IconButton onClick={handleMouseLeave} sx={{ color: "white" }}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {links.map(({ text, icon, to }) => (
            <ListItem key={text} disablePadding sx={{ display: "block" }}>
              <Link
                to={to}
                className="no-underline text-[#4880FF]"
                onClick={() => setActiveTab(to)}
              >
                <CustomTooltip title={text} placement="right" arrow>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      backgroundColor:
                        activeTab === to
                          ? theme.palette.action.selected
                          : "inherit",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                        color:
                          activeTab === to
                            ? theme.palette.primary.main
                            : "inherit",
                        borderRadius: "10%",
                        paddingX: "10px",
                        paddingY: "10px",
                        transition: "all 0.3s",
                        "&:hover": {
                          border: `2px solid ${theme.palette.primary.main}`,
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </CustomTooltip>
              </Link>
            </ListItem>
          ))}
        </List>
        <Divider />
        <Box sx={{ flexGrow: 1 }} />{" "}
        <Box>
          {open ? (
            <button
              onClick={handleLogout}
              className="bg-[#4880FF] w-full text-white py-3 "
            >
              Logout
            </button>
          ) : (
            <div
              onClick={handleLogout}
              className="w-full text-center bg-blue-500 py-2 text-white cursor-pointer "
            >
              <LogoutIcon />
            </div>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
